import React, { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Confetti } from "@/components/confetti";
import { Users, Heart, RotateCcw, Grid2x2, ArrowRight, MessagesSquare, MessageCircle, Flame, Feather, Target, Shield, Brain, Zap, ScrollText, Snowflake, Ghost, EyeOff, Handshake } from "lucide-react";

const GRADIENTS = [
  ["#36B795", "#155E4C"],
  ["#E0688F", "#8A2F50"],
  ["#9B5FB0", "#4F2C61"],
  ["#8579E0", "#3E37A0"],
];
const QUIZ_ICON = { communication: MessagesSquare, texting: MessageCircle, conflict: Flame };
const ARCHETYPE_ICON = {
  peacemaker: Feather, shooter: Target, guardian: Shield, overthinker: Brain,
  instant: Zap, novelist: ScrollText, dry: Snowflake, ghost: Ghost,
  avoider: EyeOff, resolver: Handshake, eruptor: Flame, freezer: Snowflake,
};
const Ico = (key, map, fallback, props) => React.createElement(map[key] || fallback, props);
const QUESTIONS_PER_RUN = 6;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
function matchPercent(a, b) {
  const s = [a, b].sort().join("|");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return 64 + (h % 33);
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-4 py-8">
      <Link to="/" className="flex items-center gap-2 mb-6">
        <span className="text-xl font-semibold" style={{ fontFamily: '"Fraunces", serif' }}>Pairvio</span>
      </Link>
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}

export function QuizPublicScreen() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const list = t("quizzes.list", { returnObjects: true });

  const inviterQuiz = params.get("q");
  const inviterType = params.get("a");
  const isCompareInvite = inviterQuiz && inviterType && list[inviterQuiz];

  const [stage, setStage] = useState(isCompareInvite ? "compareIntro" : "hub");
  const [quizId, setQuizId] = useState(isCompareInvite ? inviterQuiz : null);
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const compareMode = isCompareInvite;

  const quiz = quizId ? list[quizId] : null;
  const total = questions.length;

  const result = useMemo(() => {
    if (!quiz || answers.length < total || total === 0) return null;
    const tally = {};
    answers.forEach((type) => (tally[type] = (tally[type] || 0) + 1));
    return Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0];
  }, [answers, total, quiz]);

  const begin = (id) => {
    setQuizId(id);
    setQuestions(shuffle(list[id].pool).slice(0, QUESTIONS_PER_RUN));
    setAnswers([]);
    setIndex(0);
    setStage("quiz");
  };
  const pick = (type) => {
    const next = [...answers, type];
    setAnswers(next);
    if (index + 1 < total) setIndex(index + 1);
    else setStage(compareMode ? "compare" : "result");
  };

  const DownloadCta = () => (
    <Button className="w-full mt-4" asChild>
      <Link to="/">{t("quizzes.download")} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
    </Button>
  );

  // COMPARE INTRO (invitee landing)
  if (stage === "compareIntro") {
    const q = list[inviterQuiz];
    const arch = q.archetypes[inviterType] || Object.values(q.archetypes)[0];
    return (
      <Shell>
        <div className="rounded-2xl border bg-card p-6 text-center">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2">{t("quizzes.poweredBy")}</p>
          <h1 className="text-xl font-semibold mb-4">{q.name}</h1>
          <div className="flex justify-center mb-2">{Ico(inviterType, ARCHETYPE_ICON, Feather, { className: "h-12 w-12 text-primary", strokeWidth: 2 })}</div>
          <p className="text-sm text-muted-foreground mb-1">{t("quizzes.theyAre")}</p>
          <p className="text-2xl font-semibold mb-5">{arch.name}</p>
          <p className="text-muted-foreground mb-5">{t("quizzes.compareIntro")}</p>
          <Button className="w-full" onClick={() => begin(inviterQuiz)}>{t("quizzes.start")} <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
        </div>
      </Shell>
    );
  }

  // HUB
  if (stage === "hub") {
    return (
      <Shell>
        <h1 className="text-2xl font-semibold mb-1">{t("quizzes.hubTitle")}</h1>
        <p className="text-muted-foreground mb-5">{t("quizzes.hubSubtitle")}</p>
        <div className="space-y-3">
          {Object.keys(list).map((id, i) => {
            const q = list[id];
            const g = GRADIENTS[i % GRADIENTS.length];
            return (
              <button key={id} onClick={() => begin(id)} className="w-full flex items-center gap-4 rounded-xl border bg-card p-4 text-left hover:shadow-md transition">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: g[0] + "22" }}>{Ico(id, QUIZ_ICON, MessagesSquare, { className: "h-6 w-6", style: { color: g[1] }, strokeWidth: 2.2 })}</span>
                <span className="flex-1">
                  <span className="block font-semibold">{q.name}</span>
                  <span className="block text-sm text-muted-foreground">{q.blurb}</span>
                </span>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      </Shell>
    );
  }

  // QUIZ
  if (stage === "quiz") {
    const q = questions[index];
    return (
      <Shell>
        {compareMode && (
          <div className="flex items-center gap-2 rounded-xl border border-pink-300 bg-pink-50 dark:bg-pink-950/30 p-3 mb-4 text-sm font-medium text-pink-800 dark:text-pink-200">
            <Users className="h-4 w-4" /> {t("quizzes.shareEyebrow")}
          </div>
        )}
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden mb-2">
          <div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${(index / total) * 100}%` }} />
        </div>
        <p className="text-sm text-muted-foreground mb-5">{t("quizzes.progress", { n: index + 1, total })}</p>
        <h2 className="text-xl font-semibold mb-5">{q.q}</h2>
        <div className="space-y-3">
          {q.options.map((opt, i) => (
            <button key={i} onClick={() => pick(opt.type)} className="w-full rounded-xl border bg-card p-4 text-left font-medium hover:border-primary hover:shadow-sm transition">
              {opt.label}
            </button>
          ))}
        </div>
      </Shell>
    );
  }

  // COMPARE RESULT
  if (stage === "compare") {
    const q = list[inviterQuiz];
    const order = q.order || Object.keys(q.archetypes);
    const mine = q.archetypes[inviterType];
    const theirs = q.archetypes[result];
    const pct = matchPercent(inviterType, result);
    const g = GRADIENTS[Math.max(0, order.indexOf(result)) % GRADIENTS.length];
    return (
      <Shell>
        <Confetti count={22} />
        <div className="rounded-2xl p-6 text-white text-center" style={{ background: `linear-gradient(135deg, ${g[0]}, ${g[1]})` }}>
          <p className="text-xs font-bold uppercase tracking-wide opacity-80 mb-3">{q.name}</p>
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex-1">
              <div className="flex justify-center">{Ico(inviterType, ARCHETYPE_ICON, Feather, { className: "h-9 w-9", strokeWidth: 2 })}</div>
              <div className="font-semibold text-sm mt-1">{mine.name}</div>
            </div>
            <Heart className="h-6 w-6" />
            <div className="flex-1">
              <div className="flex justify-center">{Ico(result, ARCHETYPE_ICON, Feather, { className: "h-9 w-9", strokeWidth: 2 })}</div>
              <div className="font-semibold text-sm mt-1">{theirs.name}</div>
            </div>
          </div>
          <div className="text-5xl font-bold">{pct}%</div>
          <div className="text-xs font-semibold uppercase tracking-widest opacity-85 mt-1">{t("quizzes.matchLabel")}</div>
        </div>
        <DownloadCta />
        <button onClick={() => begin(quizId)} className="w-full mt-3 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <RotateCcw className="h-4 w-4" /> {t("quizzes.retake")}
        </button>
      </Shell>
    );
  }

  // SOLO RESULT
  const order = quiz.order || Object.keys(quiz.archetypes);
  const arch = quiz.archetypes[result] || quiz.archetypes[order[0]];
  const g = GRADIENTS[Math.max(0, order.indexOf(result)) % GRADIENTS.length];
  return (
    <Shell>
      <Confetti count={22} />
      <div className="rounded-2xl p-6 text-white" style={{ background: `linear-gradient(135deg, ${g[0]}, ${g[1]})` }}>
        <p className="text-xs font-bold uppercase tracking-wide opacity-80 mb-3">{quiz.name}</p>
        <div className="mb-3">{Ico(result, ARCHETYPE_ICON, Feather, { className: "h-12 w-12", strokeWidth: 2 })}</div>
        <h1 className="text-3xl font-bold mb-3">{arch.name}</h1>
        <p className="opacity-95 leading-relaxed">{arch.desc}</p>
      </div>
      <DownloadCta />
      <div className="flex justify-center gap-6 mt-4 text-sm text-muted-foreground">
        <button onClick={() => begin(quizId)} className="flex items-center gap-2"><RotateCcw className="h-4 w-4" /> {t("quizzes.retake")}</button>
        <button onClick={() => setStage("hub")} className="flex items-center gap-2"><Grid2x2 className="h-4 w-4" /> {t("quizzes.another")}</button>
      </div>
    </Shell>
  );
}
